import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/* ── Toast Notification System ── */
export const ToastContext = createContext({ addToast: () => {} });
export function useToast() { return useContext(ToastContext); }

// Post-#663 hardening: two background sources can dispatch toasts on a timer
// during a real outage — flushPendingSaves (api-client.js, up to 1/30s PER
// TABLE) and mergeByID via the WS/poll path (App.jsx, up to 1/15s PER TABLE).
// A sustained outage could previously pile up toasts forever (unbounded array
// → unbounded memory + an ever-growing on-screen stack). MAX_TOASTS caps how
// many are stored/shown at once; the OLDEST are dropped first so the newest
// (freshest context) always survive. Auto-hide timers are unaffected: a timer
// firing for an already-evicted id is a harmless no-op (filter finds nothing).
const MAX_TOASTS = 5;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // 2026-07-17 timer-leak fix (Roster 3fa233fc class / CI run 29554705338): every
  // toast armed an untracked auto-dismiss setTimeout whose setToasts tail survived
  // ToastProvider unmount. The provider wraps every page test and toasts fire in
  // most of them — the most massive instance of the class: on a slow 1-worker CI
  // box a tail firing after jsdom teardown → uncaught ReferenceError → exit 1 with
  // all tests green. One timer lives PER toast (no clear-on-re-arm, unlike
  // single-flash timers): ids are tracked in a Set, a fired timer removes its own
  // id (bounded growth), unmount clears whatever is still pending.
  const dismissTimersRef = useRef(new Set());
  const addToast = useCallback((message, type = "info", duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }].slice(-MAX_TOASTS));
    const timerId = setTimeout(() => {
      dismissTimersRef.current.delete(timerId);
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
    dismissTimersRef.current.add(timerId);
  }, []);
  useEffect(() => () => { for (const t of dismissTimersRef.current) clearTimeout(t); }, []);

  const colors = {
    success: { bg: "rgba(16,185,129,0.12)", border: "#10B981", color: "#34D399", icon: "✅" },
    error: { bg: "rgba(239,68,68,0.12)", border: "#EF4444", color: "#F87171", icon: "❌" },
    warning: { bg: "rgba(245,158,11,0.12)", border: "#F59E0B", color: "#FBBF24", icon: "⚠️" },
    info: { bg: "rgba(56,189,248,0.12)", border: "#38BDF8", color: "#7DD3FC", icon: "ℹ️" },
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* QW6 (a11y): aria-live so screen readers announce toasts as they appear. polite =
          wait for a pause; atomic=false = announce only the new toast, not the whole stack. */}
      <div aria-live="polite" aria-atomic="false" style={{ position: "fixed", top: "max(16px, env(safe-area-inset-top, 16px))", right: "max(16px, env(safe-area-inset-right, 16px))", zIndex: 999999, display: "flex", flexDirection: "column", gap: 8, maxWidth: 380, pointerEvents: "none" }}> {/* FIX 3: iPhone notch/Dynamic Island */}
        {toasts.map(t => {
          const c = colors[t.type] || colors.info;
          return (
            <div key={t.id} style={{ padding: "12px 18px", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, color: c.color, fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,0.25)", backdropFilter: "blur(16px)", display: "flex", alignItems: "center", gap: 10, animation: "slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)", pointerEvents: "auto", minWidth: 220 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{c.icon}</span>
              <span style={{ flex: 1 }}>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
